import React, { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import './ToolTip.css'
import { ToolTipProps } from './ToolTip.d'
import { createPopper } from '@popperjs/core'
import { Card } from '@toasttab/shared/src/utils/craftUtils'
import { renderRichText } from 'gatsby-source-contentful/rich-text'

const ToolTip = ({
  tip,
  elevation = 'Raised',
  backgroundColor = 'bg-white',
  exitUI = 'icon',
  placement = 'top',
  arrow = true,
  wide = false,
  tipClass,
  tipContainerClass,
  uid
}: ToolTipProps) => {
  const [showToolTip, setShowToolTip] = useState(false)
  const [showPopupMobile, setShowPopupMobile] = useState(false)
  const [isToolTipActive, setIsToolTipActive] = useState(false)
  const [isIconActive, setIsIconActive] = useState(false)
  const popperRef = useRef(null)
  const arrowRef = useRef(null)
  const toolTipRef = useRef(null)
  const isToolTipActiveRef = useRef(false)
  const isIconActiveRef = useRef(false)

  useEffect(() => {
    isToolTipActiveRef.current = isToolTipActive
  }, [isToolTipActive])

  useEffect(() => {
    isIconActiveRef.current = isIconActive
  })

  useEffect(() => {
    createPopper(popperRef.current, toolTipRef.current, {
      placement: placement,
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 8]
          }
        },
        {
          name: 'arrow',
          options: {
            element: arrowRef.current
          }
        }
      ]
    })
  }, [])

  const toggleToolTip = () => {
    setShowPopupMobile(!showPopupMobile)
    setShowToolTip(!showToolTip)
  }

  const toolTipContainer = classNames(tipContainerClass, {
    'flex cursor-pointer tooltip ': true,
    'before:content-[""] before:fixed before:block before:top-0 before:bottom-0 before:left-0 before:right-0 before:z-[50] lg:before:!hidden':
      showPopupMobile,
    'before:bg-[rgba(0,0,0,0.6)]': backgroundColor === 'bg-white'
  })

  const toolTipClasses = classNames(
    'caption w-full text-xs',
    {
      'text-white':
        backgroundColor === 'bg-gray-125' || backgroundColor === 'bg-gray-25'
    },
    tipClass
  )

  const toolTipPopperClasses = classNames(
    backgroundColor,
    Card.elevation(elevation),
    {
      ' p-4 lg:px-3 lg:py-2 rounded-lg text-center block p-6 rounded-lg hidden lg:block opacity-1 relative cursor-text ':
        true,
      'w-[174px]': !wide,
      'w-[274px]': wide,
      'lg:opacity-1 z-[120] h-auto': showToolTip,
      'lg:opacity-0 -z-[1] h-0': !showToolTip
    }
  )

  const arrowClasses = classNames({
    'tooltip-arrow w-2 h-2 absolute before:content-[""] before:absolute before:w-2 before:h-2 before:left-1/2 before:-translate-x-1/2 before:rotate-[40deg] before:z-10 before:bg-inherit':
      true,
    'before:bg-white': backgroundColor === 'bg-white',
    'before:bg-gray-125': backgroundColor === 'bg-gray-125',
    'before:bg-gray-25': backgroundColor === 'bg-gray-25',
    'top-0': placement === 'bottom',
    'bottom-0': placement === 'top',
    '-right-1': placement === 'left',
    'left-1': placement === 'right'
  })

  const toolTipMobileContainer = classNames(
    backgroundColor,
    Card.elevation(elevation),
    {
      'rounded-lg p-4 lg:!hidden w-[calc(100vw_-_32px)] fixed mx-auto top-1/2 left-[10px] w-[calc(100%-20px)]':
        true,
      'justify-between items-center': exitUI === 'icon',
      'flex-col': exitUI === 'link',
      'flex z-[100] lg:!z-0': showPopupMobile,
      hidden: !showPopupMobile
    }
  )

  const toolTipMobileClasses = classNames(toolTipClasses, {
    'mr-4': exitUI === 'icon',
    'mb-4': exitUI === 'link'
  })

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowPopupMobile(false)
    setShowToolTip(false)
  }

  const handleCloseKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      setShowPopupMobile(false)
      setShowToolTip(false)
    }
  }

  const renderExitUI = () => {
    switch (exitUI) {
      case 'icon':
        return (
          <div
            aria-expanded={showToolTip}
            aria-label='Close tooltip'
            className='cursor-pointer'
            onClick={handleCloseClick}
            onKeyDown={handleCloseKeyDown}
            role='button'
            tabIndex={0}
          >
            <svg
              height='24'
              version='1.1'
              viewBox='0 0 1024 1024'
              width='24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M777.856 280.192l-33.92-33.952-231.872 231.872-231.84-231.872-33.984 33.888 231.872 231.904-231.84 231.84 33.888 33.984 231.904-231.904 231.84 231.872 33.952-33.888-231.872-231.904z'
                fill={
                  backgroundColor === 'bg-gray-125' ||
                  backgroundColor === 'bg-gray-25'
                    ? '#fff'
                    : '#666'
                }
              />
            </svg>
          </div>
        )
      case 'link':
        return (
          <button
            className={`${toolTipClasses} cursor-pointer underline font-semibold`}
            onClick={handleCloseClick}
            onKeyDown={handleCloseKeyDown}
            tabIndex={0}
          >
            Dismiss
          </button>
        )
      default:
        return
    }
  }

  return (
    <>
      <button
        aria-describedby={`tooltip-description-${uid}`}
        aria-expanded={showToolTip}
        aria-label='Show tooltip'
        className={toolTipContainer}
        data-testid='tooltip'
        onClick={() => toggleToolTip()}
        ref={popperRef}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggleToolTip()
          }
        }}
        onMouseEnter={() => {
          setShowToolTip(true)
          setIsIconActive(true)
        }}
        onMouseLeave={() => {
          setIsIconActive(false)
          setTimeout(() => {
            if (!isToolTipActiveRef.current && !isIconActiveRef.current) {
              setShowToolTip(false)
            }
          }, 250)
        }}
      >
        <svg
          aria-hidden={!showToolTip}
          fill='none'
          height='16px'
          role='img'
          viewBox='0 0 14 14'
          width='16px'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M7.0332 13.0334c3.3137 0 6-2.6862 6-5.99995 0-3.31371-2.6863-6-6-6-3.31371 0-6 2.68629-6 6 0 3.31375 2.68629 5.99995 6 5.99995zM7.0332 6.83325v3.33335'
            stroke='#666'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeMiterlimit='10'
          />
          <path
            d='M7.03288 5.16659c.36819 0 .66666-.29848.66666-.66667 0-.36819-.29847-.66667-.66666-.66667s-.66667.29848-.66667.66667c0 .36819.29848.66667.66667.66667z'
            fill='#666'
          />
        </svg>
        <div
          aria-live='polite'
          className={toolTipMobileContainer}
          id={`tooltip-description-${uid}`}
          role='tooltip'
        >
          {!tip.raw && (
            <>
              <div
                className={toolTipMobileClasses}
                dangerouslySetInnerHTML={{ __html: tip }}
              />
              {renderExitUI()}
            </>
          )}
          {tip.raw ? (
            <>
              <div className={toolTipMobileClasses}>{renderRichText(tip)}</div>
              {renderExitUI()}
            </>
          ) : null}
        </div>
      </button>
      <div
        aria-hidden={!showToolTip}
        className={toolTipPopperClasses}
        ref={toolTipRef}
        role='tooltip'
        onMouseEnter={() => {
          setIsToolTipActive(true)
        }}
        onMouseLeave={() => {
          setIsToolTipActive(false)
          setShowToolTip(false)
        }}
        {...(!showToolTip && { inert: '' })}
      >
        {!tip.raw && (
          <>
            <div
              className={toolTipClasses}
              dangerouslySetInnerHTML={{ __html: tip }}
            />
            {arrow ? (
              <div
                aria-hidden={!showToolTip}
                className={arrowClasses}
                ref={arrowRef}
              />
            ) : null}
          </>
        )}
        {tip.raw ? (
          <>
            <div className={toolTipClasses}>{renderRichText(tip)}</div>
            {arrow ? (
              <div
                aria-hidden={!showToolTip}
                className={arrowClasses}
                ref={arrowRef}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </>
  )
}

export default ToolTip
